import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, Booking } from '@app/core/services/admin.service';
import { MessageService } from '@app/core/services/message.service';
import { ToastService } from '@app/core/services/toast.service';
import { catchError, retry, timeout, finalize } from 'rxjs/operators';
import { of, interval } from 'rxjs';

@Component({
  selector: 'app-bookings-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bookings-list.component.html',
  styleUrls: ['./bookings-list.component.scss']
})
export class BookingsListComponent implements OnInit {
  @Output() selectBooking = new EventEmitter<Booking>();
  
  bookings: Booking[] = [];
  bookingFilter = 'all';
  bookingsLoading = true;
  selectedBookingId: string | null = null;
  unreadMessagesCount = 0;
  bookingsWithUnread: any[] = [];
  showDeleteModal = false;
  bookingToDelete: Booking | null = null;
  deletingBooking = false;

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadBookings();
    this.loadUnreadMessages();
    
    // Listen for reload events
    window.addEventListener('reload-bookings', () => {
      this.loadBookings();
      this.loadUnreadMessages();
    });
    
    // Poll for new messages every 30 seconds
    interval(30000).subscribe(() => {
      this.loadUnreadMessages();
    });
  }
  
  loadUnreadMessages() {
    console.log('[BookingsList] Loading unread messages...');
    this.messageService.getUnreadMessagesCount().subscribe({
      next: (result) => {
        console.log('[BookingsList] Unread count:', result.count);
        this.unreadMessagesCount = result.count;
      },
      error: (error) => {
        console.error('[BookingsList] Error loading unread messages:', error);
      }
    });
    
    this.messageService.getBookingsWithUnreadMessages().subscribe({
      next: (bookings) => {
        console.log('[BookingsList] Bookings with unread messages:', bookings);
        this.bookingsWithUnread = bookings;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[BookingsList] Error loading bookings with unread messages:', error);
      }
    });
  }

  loadBookings() {
    this.bookingsLoading = true;
    this.adminService.getAllBookings()
      .pipe(
        timeout(5000),
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error('Error loading bookings:', error);
          this.toastService.error('Fehler beim Laden der Buchungen');
          this.bookingsLoading = false;
          return of([] as Booking[]);
        })
      )
      .subscribe({
        next: (bookings) => {
          console.log('Bookings loaded:', bookings.length, 'bookings');
          console.log('First booking:', bookings[0]);
          this.bookings = bookings;
          this.bookingsLoading = false;
          this.cdr.detectChanges();
          console.log('After detectChanges - Loading:', this.bookingsLoading, 'Total:', this.bookings.length);
        },
        error: (err) => {
          console.error('Subscribe error:', err);
          this.bookingsLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          console.log('Bookings loading complete');
        }
      });
  }

  onSelectBooking(booking: Booking) {
    this.selectedBookingId = booking.id || null;
    this.selectBooking.emit(booking);
    
    // Reload unread count after selecting a booking (it might be marked as read)
    setTimeout(() => this.loadUnreadMessages(), 1000);
  }
  
  hasUnreadMessages(bookingId: string): boolean {
    const hasUnread = this.bookingsWithUnread.some(b => b.id === bookingId);
    console.log('[BookingsList] hasUnreadMessages for', bookingId, ':', hasUnread, 'bookingsWithUnread:', this.bookingsWithUnread);
    return hasUnread;
  }
  
  getUnreadCount(bookingId: string): number {
    const booking = this.bookingsWithUnread.find(b => b.id === bookingId);
    const count = booking ? booking.unreadCount : 0;
    console.log('[BookingsList] getUnreadCount for', bookingId, ':', count);
    return count;
  }

  getFilteredBookings() {
    if (this.bookingFilter === 'all') return this.bookings;
    return this.bookings.filter(b => this.getStatusString(b.status)?.toLowerCase() === this.bookingFilter);
  }

  getStatusString(status: any): string {
    // Handle numeric enum values from backend
    const statusMap: any = {
      '0': 'New',
      '1': 'Confirmed',
      '2': 'Cancelled'
    };
    
    // If it's a number or numeric string, convert it
    if (typeof status === 'number' || !isNaN(Number(status))) {
      return statusMap[String(status)] || 'New';
    }
    
    // If it's already a string, return it
    if (typeof status === 'string') {
      return status;
    }
    
    return 'New';
  }

  getStatusColor(status: any): string {
    const statusStr = this.getStatusString(status).toLowerCase();
    switch (statusStr) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatBookingId(id?: string): string {
    if (!id) return '';
    if (id.startsWith('VW-')) return id;
    
    const year = new Date().getFullYear();
    
    // Handle DB Format: "bookings/97-A" or "97-A"
    let cleanId = id.replace('bookings/', '');
    cleanId = cleanId.replace('-A', ''); // Remove suffix like -A, -B etc
    
    // Pad number with zeros to 3 digits
    const number = cleanId.trim().padStart(3, '0');
    return `VW-${year}-${number}`;
  }

  openDeleteModal(booking: Booking, event?: MouseEvent) {
    event?.stopPropagation();
    this.bookingToDelete = booking;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    if (this.deletingBooking) return;
    this.showDeleteModal = false;
    this.bookingToDelete = null;
  }

  confirmDeleteBooking() {
    if (!this.bookingToDelete?.id || this.deletingBooking) return;

    this.deletingBooking = true;
    this.adminService.deleteBooking(this.bookingToDelete.id).subscribe({
      next: () => {
        const deletedId = this.bookingToDelete?.id;
        this.bookings = this.bookings.filter(b => b.id !== deletedId);
        if (this.selectedBookingId === deletedId) {
          this.selectedBookingId = null;
        }
        this.toastService.success('Buchung endgültig gelöscht');
        this.deletingBooking = false;
        this.closeDeleteModal();
        this.loadUnreadMessages();
      },
      error: () => {
        this.deletingBooking = false;
        this.toastService.error('Fehler beim Löschen der Buchung');
      }
    });
  }
}
